/**
 * Node Types Registry - Exports all custom node components
 *
 * IMPORTANT: nodeTypes must be memoized to prevent re-registration on every render.
 * @see https://reactflow.dev/learn/customization/custom-nodes
 */

import { useMemo } from 'react';
import { StartNode } from './StartNode';
import { EndNode } from './EndNode';
import { AgentNode } from './AgentNode';
import { ToolNode } from './ToolNode';
import { ConditionNode } from './ConditionNode';
import { OrchestratorNode } from './OrchestratorNode';
import { SystemNode } from './SystemNode';

// Export all node components
export { StartNode } from './StartNode';
export { EndNode } from './EndNode';
export { AgentNode } from './AgentNode';
export { ToolNode } from './ToolNode';
export { ConditionNode } from './ConditionNode';
export { OrchestratorNode } from './OrchestratorNode';
export { SystemNode } from './SystemNode';

/**
 * Hook to get memoized node types for React Flow
 * This is critical for performance - prevents node re-registration
 */
export const useNodeTypes = () => {
  return useMemo(
    () => ({
      start: StartNode,
      end: EndNode,
      agent: AgentNode,
      tool: ToolNode,
      condition: ConditionNode,
      orchestrator: OrchestratorNode,
      system: SystemNode
    }),
    []
  );
};

/**
 * Node type keys for type safety
 */
export type NodeTypeKey = 'start' | 'end' | 'agent' | 'tool' | 'condition' | 'orchestrator' | 'system';

/**
 * Node types object (static version for non-React contexts)
 */
export const nodeTypes = {
  start: StartNode,
  end: EndNode,
  agent: AgentNode,
  tool: ToolNode,
  condition: ConditionNode,
  orchestrator: OrchestratorNode,
  system: SystemNode
} as const;
